// The audit log (Phase 3 · D11, #637): what happened in this workspace, read
// under the reader's own context.
//
// Written to `GET /api/v1/audit` (`api/audit.ts`). The screen is the workspace's
// trail — or, in an organisation's context, the organisation's own rows — and it
// says which context it is showing, because "no rows" and "not your context" are
// different facts.
//
// A row reads as a person would say it (#812): who acted by name, as the route
// resolved them for this context, with the stored actor on hover; the action's
// label under its category's icon, with the event name on hover; and the detail as
// key and value pairs. Nothing on the screen is JSON.
//
// The log is filtered on the server by who acted, by category and by dates, a page at
// a time, and the same filter downloads as a CSV (#813).

import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";

import {
  NO_FILTER,
  auditCsvPath,
  readAudit,
  readAuditActors,
  type ActorDisplay,
  type AuditActor,
  type AuditFilter,
  type AuditPage as Page,
  type AuditRow,
} from "../api/audit";
import { browserApi, messageOf, type Api } from "../api/http";
import { Empty, Notice, PageIntro } from "./chrome";
import { readableTime as time } from "./runPresentation";

/** What each category is called beside its icon, and the icon's path (24-unit box). */
export const CATEGORIES: Record<string, { name: string; path: string }> = {
  "sign-in": { name: "Sign-in", path: "M10 17l5-5-5-5 M15 12H3 M14 3h5a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-5" },
  account: { name: "Account", path: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8 M4 21a8 8 0 0 1 16 0" },
  access: { name: "Access", path: "M15 7a4 4 0 1 1-3.9 5H3v3h3v3h3v-3h2.1A4 4 0 0 1 15 7z" },
  consent: { name: "Consent", path: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18 M8 12l3 3 5-6" },
  change: { name: "Change", path: "M12 3l8 4v5c0 5-8 9-8 9s-8-4-8-9V7z M8 12l3 3 5-6" },
  admin: { name: "Admin", path: "M12 3l2.5 5 5.5.8-4 3.9.9 5.5L12 15.6 7.1 18.2l.9-5.5-4-3.9 5.5-.8z" },
  integration: { name: "Integration", path: "M9 7V3 M15 7V3 M6 7h12v4a6 6 0 0 1-12 0z M12 17v4" },
};
const OTHER = { name: "Other", path: "M4 4h16v16H4z" };

/** A name made readable rather than shown raw: an event with no label, a detail key. */
function readable(action: string): string {
  const words = action.replace(/[._]/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The line under an actor's name: what kind of actor, or their address. */
function actorNote(display: ActorDisplay): string | null {
  switch (display.kind) {
    case "member":
      return display.email !== null && display.email !== display.name ? display.email : null;
    case "api_key":
      return "API key";
    case "organisation":
      return "Partner";
    case "workspace":
      return "Workspace";
    default:
      return null;
  }
}

const displayName = (display: ActorDisplay): string =>
  display.kind === "removed" ? `${display.name} (removed)` : display.name;

function Identity({ stored, display }: { stored: string; display: ActorDisplay }) {
  const note = actorNote(display);
  return (
    <td title={stored}>
      {displayName(display)}
      {note === null ? null : <small>{note}</small>}
    </td>
  );
}

function Actor({ row }: { row: AuditRow }) {
  const display = row.actor_display ?? { kind: "unknown", name: row.actor, email: null };
  return <Identity stored={row.actor} display={display} />;
}

function Target({ row }: { row: AuditRow }) {
  return row.target_display == null ? <td>{row.target ?? "—"}</td> : (
    <Identity stored={row.target ?? ""} display={row.target_display} />
  );
}

function CategoryIcon({ path }: { path: string }) {
  return (
    <svg
      className="audit-icon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

function Action({ row }: { row: AuditRow }) {
  const category = CATEGORIES[row.action_category ?? ""] ?? OTHER;
  return (
    <td title={row.action}>
      <span className="audit-action">
        <CategoryIcon path={category.path} />
        {row.action_label ?? readable(row.action)}
      </span>
      <small>{category.name}</small>
    </td>
  );
}

function DetailValue({ value }: { value: unknown }): ReactNode {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return "None";
    return value.map((item, index) => (
      <Fragment key={index}>
        {index > 0 ? ", " : null}
        <DetailValue value={item} />
      </Fragment>
    ));
  }
  if (typeof value === "object") return <Detail detail={value as Record<string, unknown>} />;
  return String(value);
}

/** The detail as key and value pairs, a nested object as its own list inside the value. */
export function Detail({ detail }: { detail: Record<string, unknown> }) {
  const entries = Object.entries(detail);
  if (entries.length === 0) return <>—</>;
  return (
    <dl className="audit-detail">
      {entries.map(([key, value]) => (
        <div key={key}>
          <dt>{readable(key)}</dt>
          <dd>
            <DetailValue value={value} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Stored actors the screen names alike, offered as one choice: every support job is one. */
export interface ActorChoice {
  label: string;
  actors: string[];
}

/**
 * A purged account's choice is told apart by the start of its stored id (#1173): the
 * account no longer has a name or an address, and nothing else on it is the person's.
 */
const storedIdNote = (actor: string): string => actor.slice(actor.indexOf(":") + 1).slice(0, 8);

/** The actors the filter offers, one choice per name, in the order the route gave them. */
export function actorChoices(actors: AuditActor[]): ActorChoice[] {
  const byLabel = new Map<string, ActorChoice>();
  for (const { actor, actor_display } of actors) {
    const display = actor_display ?? { kind: "unknown", name: actor, email: null };
    const note = display.kind === "deleted" ? storedIdNote(actor) : actorNote(display);
    const name = displayName(display);
    const label = note === null ? name : `${name} (${note})`;
    const choice = byLabel.get(label);
    if (choice === undefined) byLabel.set(label, { label, actors: [actor] });
    else choice.actors.push(actor);
  }
  return [...byLabel.values()];
}

const sameActors = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((actor, index) => actor === b[index]);

const filtered = (filter: AuditFilter): boolean =>
  filter.actors.length > 0 || filter.category !== "" || filter.from !== "" || filter.to !== "";

/** The console's audit log: the first page on arrival, and a new first page per filter. */
export function AuditPage({ api = browserApi }: { api?: Api }) {
  const [filter, setFilter] = useState<AuditFilter>(NO_FILTER);
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [actors, setActors] = useState<AuditActor[]>([]);
  // The filter the rows on screen were read under: an older page read under another is dropped.
  const shownFilter = useRef<AuditFilter>(filter);

  useEffect(() => {
    let live = true;
    void readAuditActors(api).then(
      (value) => live && setActors(value),
      // The filter offers nobody; the log itself says whether it could be read.
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [api]);

  useEffect(() => {
    let live = true;
    shownFilter.current = filter;
    setLoadingMore(false);
    setRows(null);
    setNext(null);
    setError(null);
    void readAudit(api, filter).then(
      (page) => {
        if (!live) return;
        setRows(page.items);
        setNext(page.next_cursor);
      },
      (reason: unknown) => live && setError(messageOf(reason)),
    );
    return () => {
      live = false;
    };
  }, [api, filter]);

  const more = () => {
    if (next === null) return;
    const asked = filter;
    const current = () => shownFilter.current === asked;
    setLoadingMore(true);
    readAudit(api, asked, next).then(
      (page: Page) => {
        if (!current()) return;
        setRows((shown) => [...(shown ?? []), ...page.items]);
        setNext(page.next_cursor);
        setLoadingMore(false);
      },
      (reason: unknown) => {
        if (!current()) return;
        setError(messageOf(reason));
        setLoadingMore(false);
      },
    );
  };

  return (
    <AuditLog
      rows={rows}
      error={error}
      filter={filter}
      actors={actors}
      next={next}
      loadingMore={loadingMore}
      onFilter={setFilter}
      onMore={more}
    />
  );
}

/** The screen over rows already read, for a render with no API behind it. */
export function AuditView({
  rows,
  filter = NO_FILTER,
  actors = [],
  next = null,
}: {
  rows: AuditRow[];
  filter?: AuditFilter;
  actors?: AuditActor[];
  next?: string | null;
}) {
  return (
    <AuditLog
      rows={rows}
      error={null}
      filter={filter}
      actors={actors}
      next={next}
      loadingMore={false}
      onFilter={() => undefined}
      onMore={() => undefined}
    />
  );
}

function AuditLog({
  rows,
  error,
  filter,
  actors,
  next,
  loadingMore,
  onFilter,
  onMore,
}: {
  rows: AuditRow[] | null;
  error: string | null;
  filter: AuditFilter;
  actors: AuditActor[];
  next: string | null;
  loadingMore: boolean;
  onFilter: (filter: AuditFilter) => void;
  onMore: () => void;
}) {
  return (
    <section className="stack" aria-label="Audit log">
      <PageIntro
        destination="audit"
        action={
          <a className="btn" href={auditCsvPath(filter)} download>
            Download CSV
          </a>
        }
      />
      <section className="panel">
        <Filters filter={filter} actors={actors} onFilter={onFilter} />
        <AuditBody rows={rows} error={error} filter={filter} onFilter={onFilter} />
        {rows === null || rows.length === 0 ? null : (
          <div className="audit-footer">
            <span className="muted" role="status">
              {rows.length} {rows.length === 1 ? "entry" : "entries"} shown · newest first
            </span>
            {next === null ? (
              <span className="muted">That is every entry.</span>
            ) : (
              <button type="button" className="btn" disabled={loadingMore} onClick={onMore}>
                {loadingMore ? "Loading…" : "Load older entries"}
              </button>
            )}
          </div>
        )}
      </section>
    </section>
  );
}

function Filters({
  filter,
  actors,
  onFilter,
}: {
  filter: AuditFilter;
  actors: AuditActor[];
  onFilter: (filter: AuditFilter) => void;
}) {
  const choices = actorChoices(actors);
  const chosen = choices.findIndex((choice) => sameActors(choice.actors, filter.actors));
  const change = (patch: Partial<AuditFilter>) => onFilter({ ...filter, ...patch });
  return (
    <div className="audit-filters">
      <label className="field__label">
        Who
        <select
          className="input"
          value={chosen === -1 ? "" : String(chosen)}
          onChange={(event) =>
            change({ actors: event.target.value === "" ? [] : choices[Number(event.target.value)].actors })
          }
        >
          <option value="">Anyone</option>
          {choices.map((choice, index) => (
            <option key={choice.label} value={String(index)}>
              {choice.label}
            </option>
          ))}
        </select>
      </label>
      <label className="field__label">
        Category
        <select
          className="input"
          value={filter.category}
          onChange={(event) => change({ category: event.target.value })}
        >
          <option value="">Every category</option>
          {Object.entries(CATEGORIES).map(([key, category]) => (
            <option key={key} value={key}>
              {category.name}
            </option>
          ))}
          <option value="other">{OTHER.name}</option>
        </select>
      </label>
      <label className="field__label">
        From
        <input
          type="date"
          className="input"
          value={filter.from}
          max={filter.to || undefined}
          onChange={(event) => change({ from: event.target.value })}
        />
      </label>
      <label className="field__label">
        To
        <input
          type="date"
          className="input"
          value={filter.to}
          min={filter.from || undefined}
          onChange={(event) => change({ to: event.target.value })}
        />
      </label>
    </div>
  );
}

function AuditBody({
  rows,
  error,
  filter,
  onFilter,
}: {
  rows: AuditRow[] | null;
  error: string | null;
  filter: AuditFilter;
  onFilter: (filter: AuditFilter) => void;
}) {
  if (error !== null) {
    return <Notice tone="bad">The audit log is unavailable: {error}</Notice>;
  }
  if (rows === null) return <p className="muted panel__body" role="status">Reading the audit log…</p>;
  if (rows.length === 0) {
    return filtered(filter) ? (
      <Empty title="No matching entries">
        <p>No entries match these filters.</p>
        <button type="button" className="btn" onClick={() => onFilter(NO_FILTER)}>
          Clear filters
        </button>
      </Empty>
    ) : (
      <Empty title="No entries">No activity yet.</Empty>
    );
  }
  return (
    <div className="table-wrap" tabIndex={0}>
      <table className="table audit-log">
        <thead>
          <tr>
            <th>When</th>
            <th>Actor</th>
            <th>Action</th>
            <th>Target</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{time(row.occurred_at)}</td>
              <Actor row={row} />
              <Action row={row} />
              <Target row={row} />
              <td>
                <Detail detail={row.detail} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
